import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Ticket, Gift, CheckCircle2, Package, ChevronRight, MessageCircle, Sparkles } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

export default function Profile() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [subs, setSubs] = useState([]);
  const [accessCode, setAccessCode] = useState("");
  const [refCode, setRefCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/profile").then(r => setProfile(r.data));
    api.get("/subscriptions/me").then(r => setSubs(r.data));
  };
  useEffect(() => { load(); }, []);

  const addCoaching = () => {
    try { localStorage.setItem("coachjp_pkg", "coaching_support"); } catch { /* noop */ }
    navigate("/payment");
  };

  const [confirmCancelId, setConfirmCancelId] = useState(null);
  const cancelCoaching = async () => {
    if (!confirmCancelId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/subscriptions/${confirmCancelId}/cancel`);
      const endDate = data?.period_end ? new Date(data.period_end).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "end of paid period";
      toast.success(`Cancellation scheduled — coaching ends ${endDate}.`);
      setConfirmCancelId(null);
      await refresh();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancellation failed");
    }
    setBusy(false);
  };

  const redeemAccess = async () => {
    if (!accessCode.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/codes/redeem", { code: accessCode.trim().toUpperCase() });
      toast.success(`Access code accepted — ${data.package} unlocked!`);
      setAccessCode("");
      await refresh();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid access code");
    }
    setBusy(false);
  };

  const applyReferral = async () => {
    if (!refCode.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/referrals/apply", { code: refCode.trim().toUpperCase() });
      toast.success(`Referral applied — invited by ${data.referrer_name}. You'll both get +1 free week on your first paid subscription.`);
      setRefCode("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid referral code");
    }
    setBusy(false);
  };

  const alreadyReferred = !!profile?.referred_by;
  const hasActive = user?.has_active_subscription;
  const activeSubs = subs.filter(s => s.status === "active");
  const isAdmin = user?.role === "admin";

  return (
    <AppShell>
      <div data-testid="profile-page">
        <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">PROFILE · SETTINGS</div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase">{user?.name}</h1>
        <div className="font-mono-jp text-xs text-[#888]">{user?.email}</div>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-5">
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">ATHLETE DETAILS</div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <Pair label="Age" v={profile?.age} />
              <Pair label="Sex" v={profile?.sex} />
              <Pair label="Weight" v={profile?.weight_kg ? `${profile.weight_kg} kg` : "—"} />
              <Pair label="Height" v={profile?.height_cm ? `${profile.height_cm} cm` : "—"} />
              <Pair label="Country" v={profile?.country} />
              <Pair label="Level" v={profile?.running_level} />
              <Pair label="Goal" v={profile?.goal_race} />
              <Pair label="Goal time" v={profile?.goal_time} />
              <Pair label="5K PB" v={profile?.pb_5k} />
              <Pair label="10K PB" v={profile?.pb_10k} />
              <Pair label="Half PB" v={profile?.pb_half} />
              <Pair label="Marathon PB" v={profile?.pb_marathon} />
              <Pair label="Experience" v={EXP_LABELS[profile?.running_experience_years]} />
              <Pair label="Surface" v={profile?.training_surface ? profile.training_surface[0].toUpperCase() + profile.training_surface.slice(1) : "—"} />
              <Pair label="Races (12mo)" v={profile?.races_last_year ?? "—"} />
              <Pair label="Cross-training" v={(profile?.cross_training || []).join(", ") || "—"} />
            </div>
            <Link to="/onboarding" className="mt-4 inline-flex items-center gap-1 font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] hover:text-white" data-testid="edit-profile-link">
              Edit profile <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-5" data-testid="subscription-card">
            <div className="flex items-center justify-between gap-2">
              <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">SUBSCRIPTION</div>
              {user?.has_coaching && (
                <span className="font-mono-jp text-[9px] uppercase tracking-widest text-[#0a0a0a] bg-gradient-to-r from-[#d7ff00] to-[#a8c800] px-2 py-0.5 flex items-center gap-1" data-testid="coaching-active-badge">
                  <MessageCircle className="w-2.5 h-2.5" /> COACHING ACTIVE
                </span>
              )}
            </div>
            {activeSubs.length === 0 ? (
              <>
                <div className="mt-3 text-[#888] text-sm">No active subscription.</div>
                <Link to="/packages" className="mt-4 inline-flex items-center gap-2 bg-[#d7ff00] text-black font-display tracking-wider uppercase text-sm px-4 py-2 hover:bg-white" data-testid="view-packages-link">
                  <Package className="w-4 h-4" /> View Packages
                </Link>
              </>
            ) : (
              <div className="mt-3 space-y-2">
                {activeSubs.map(s => {
                  const isCoaching = COACHING_PKG_IDS.has(s.package_id);
                  const periodEnd = s.period_end ? new Date(s.period_end) : null;
                  const periodEndStr = periodEnd ? periodEnd.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null;
                  const cancelScheduled = !!s.cancel_requested_at;
                  return (
                    <div key={s.id} className={`border p-3 ${isCoaching ? "border-[#d7ff00]/40 bg-[#d7ff00]/5" : "border-[#222]"}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-display text-lg uppercase truncate">{s.package_name}</div>
                          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] mt-0.5">{s.currency} {s.amount}{COACHING_PKG_IDS.has(s.package_id) || s.package_id === "annual" || s.package_id === "ultimate" ? " / month" : " · one-time"}</div>
                        </div>
                        <span className={`font-mono-jp text-[9px] uppercase tracking-widest px-2 py-0.5 flex-shrink-0 ${cancelScheduled ? "text-[#ff9500] border border-[#ff9500]/60" : (s.status === "active" ? "text-[#d7ff00] border border-[#d7ff00]/40" : "text-[#888] border border-[#333]")}`}>
                          {cancelScheduled ? "ENDS SOON" : s.status?.toUpperCase()}
                        </span>
                      </div>
                      {isCoaching && cancelScheduled && periodEndStr && (
                        <div className="mt-2 font-mono-jp text-[10px] uppercase tracking-widest text-[#ff9500]">Cancellation scheduled · access until {periodEndStr}</div>
                      )}
                      {isCoaching && !cancelScheduled && periodEndStr && (
                        <div className="mt-2 font-mono-jp text-[10px] uppercase tracking-widest text-[#666]">Renews on {periodEndStr}</div>
                      )}
                      {isCoaching && !cancelScheduled && (
                        <button
                          onClick={() => setConfirmCancelId(s.id)}
                          className="mt-2 font-mono-jp text-[10px] uppercase tracking-widest text-[#888] hover:text-[#ff6b35] flex items-center gap-1"
                          data-testid={`cancel-coaching-${s.id}`}
                        >
                          Cancel coaching
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Coaching upsell — visible only if athlete has ≥1 active sub but NOT coaching (admins never see this) */}
            {!isAdmin && activeSubs.length > 0 && !user?.has_coaching && (
              <div className="mt-4 border border-dashed border-[#d7ff00]/60 bg-gradient-to-br from-[#d7ff00]/8 to-transparent p-4" data-testid="coaching-upsell">
                <div className="flex items-center gap-1.5 font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">
                  <Sparkles className="w-3 h-3" /> UPGRADE · UNLOCK COACH JP
                </div>
                <div className="mt-1.5 text-sm text-white leading-snug">
                  Add <span className="text-[#d7ff00] font-medium">Coaching Support</span> to message Coach JP directly, get training adjustments and race strategy.
                </div>
                <ul className="mt-2 space-y-1 text-xs text-[#bbb]">
                  <li className="flex items-center gap-1.5"><MessageCircle className="w-3 h-3 text-[#d7ff00]" /> Direct chat with Coach JP</li>
                  <li className="flex items-center gap-1.5"><MessageCircle className="w-3 h-3 text-[#d7ff00]" /> Training plan adjustments</li>
                  <li className="flex items-center gap-1.5"><MessageCircle className="w-3 h-3 text-[#d7ff00]" /> Race strategy + priority support</li>
                </ul>
                <button
                  onClick={addCoaching}
                  className="mt-3 w-full bg-[#d7ff00] text-black font-display tracking-wider uppercase text-sm px-4 py-2.5 hover:bg-white flex items-center justify-center gap-2"
                  data-testid="add-coaching-btn"
                >
                  Add Coaching · RM 199 / month
                </button>
                <p className="mt-2 text-[10px] font-mono-jp uppercase tracking-widest text-[#666] text-center">Cancel anytime — your program stays yours</p>
              </div>
            )}
          </div>
        </div>

        {/* Code redemption — only show if no active sub */}
        {!hasActive && (
          <div className="mt-6 bg-[#0a0a0a] border border-[#d7ff00]/40 p-5" data-testid="codes-section">
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">UNLOCK ACCESS</div>
            <p className="mt-1 text-[#bbb] text-sm">Got an access code from Coach JP or a referral code from a friend? Apply it here.</p>

            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <div>
                <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] flex items-center gap-1"><Ticket className="w-3 h-3" /> Access code</label>
                <div className="mt-2 flex gap-2">
                  <input value={accessCode} onChange={e => setAccessCode(e.target.value.toUpperCase())} placeholder="CJP-XXXX-XXXX"
                    className="flex-1 bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2.5 font-mono-jp tracking-widest" data-testid="profile-access-code" />
                  <button onClick={redeemAccess} disabled={busy || !accessCode.trim()} className="bg-[#d7ff00] text-black font-display tracking-wider uppercase px-4 py-2.5 hover:bg-white disabled:opacity-50" data-testid="redeem-access-btn">Redeem</button>
                </div>
              </div>
              <div>
                <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] flex items-center gap-1"><Gift className="w-3 h-3" /> Referral code from a friend</label>
                {alreadyReferred ? (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-[#d7ff00] font-mono-jp text-[10px] uppercase tracking-widest" data-testid="ref-already-applied">
                    <CheckCircle2 className="w-3.5 h-3.5" /> REFERRAL ALREADY APPLIED
                  </div>
                ) : (
                  <div className="mt-2 flex gap-2">
                    <input value={refCode} onChange={e => setRefCode(e.target.value.toUpperCase())} placeholder="FRIEND-XXXX"
                      className="flex-1 bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2.5 font-mono-jp tracking-widest" data-testid="profile-ref-code" />
                    <button onClick={applyReferral} disabled={busy || !refCode.trim()} className="bg-[#d7ff00] text-black font-display tracking-wider uppercase px-4 py-2.5 hover:bg-white disabled:opacity-50" data-testid="apply-ref-btn">Apply</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-10" />
      </div>

      {/* Cancel coaching confirmation modal */}
      {confirmCancelId && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" data-testid="cancel-confirm-modal" onClick={() => !busy && setConfirmCancelId(null)}>
          <div className="bg-[#0a0a0a] border border-[#ff6b35] w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#ff6b35]">CANCEL COACHING?</div>
            <h2 className="mt-2 font-display text-2xl uppercase leading-tight">Cancellation takes effect <span className="text-[#ff6b35]">at the end of your paid period</span></h2>
            <ul className="mt-4 space-y-1.5 text-sm text-[#bbb]">
              <li>· You keep <span className="text-white">coach chat &amp; training adjustments</span> until your period ends</li>
              <li>· <span className="text-white">No refund</span> for the current period</li>
              <li>· Your training program &amp; history stay yours after coaching ends</li>
              <li>· You can re-subscribe anytime</li>
            </ul>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setConfirmCancelId(null)} disabled={busy} className="flex-1 bg-[#111] border border-[#333] text-white font-display tracking-wider uppercase py-2.5 hover:border-[#d7ff00] disabled:opacity-50" data-testid="cancel-confirm-no">Keep coaching</button>
              <button onClick={cancelCoaching} disabled={busy} className="flex-1 bg-[#ff6b35] text-black font-display tracking-wider uppercase py-2.5 hover:bg-white disabled:opacity-50" data-testid="cancel-confirm-yes">{busy ? "Cancelling…" : "Confirm"}</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function Pair({ label, v }) {
  return (
    <div>
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#666]">{label}</div>
      <div className="text-white">{v ?? "—"}</div>
    </div>
  );
}

const EXP_LABELS = {
  under_1: "< 1 year",
  "1_3": "1 – 3 years",
  "3_5": "3 – 5 years",
  "5_10": "5 – 10 years",
  over_10: "10+ years",
};

// Coaching-tier packages — kept in sync with backend `COACHING_TIERS` (legacy combo packages included for grandfathered subscribers)
const COACHING_PKG_IDS = new Set([
  "coaching_support", "annual", "ultimate",
  "5k10k_coaching", "half_coaching", "marathon_coaching",
]);
